import React, {
    createContext,
    useState,
    useContext,
    ReactNode,
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
} from "react";

type HeaderBackgroundContextType = {
    hasWhiteBackground: boolean;
    setHasWhiteBackground: Dispatch<SetStateAction<boolean>>;
    hasFullBackground: boolean;
    setHasFullBackground: Dispatch<SetStateAction<boolean>>;
    mobileNavOpen: boolean;
    setMobileNavOpen: Dispatch<SetStateAction<boolean>>;
};
type HeaderBacgroundContextProp = {
    children: ReactNode;
    hasFullHero?: boolean;
};

const HeaderBackgroundContext = createContext<HeaderBackgroundContextType | undefined>({
    hasWhiteBackground: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setHasWhiteBackground: () => {},
    hasFullBackground: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setHasFullBackground: () => {},
    mobileNavOpen: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setMobileNavOpen: () => {},
});

function HeaderBackgroundProvider({
    children,
    hasFullHero = false,
}: Readonly<HeaderBacgroundContextProp>) {
    const [hasWhiteBackground, setHasWhiteBackground] = useState(false);
    const [hasFullBackground, setHasFullBackground] = useState(hasFullHero);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const contextValue = useMemo(
        () => ({
            hasWhiteBackground,
            setHasWhiteBackground,
            hasFullBackground,
            setHasFullBackground,
            mobileNavOpen,
            setMobileNavOpen,
        }),
        [
            hasWhiteBackground,
            setHasWhiteBackground,
            hasFullBackground,
            setHasFullBackground,
            mobileNavOpen,
            setMobileNavOpen,
        ]
    );

    useEffect(() => {
        setHasWhiteBackground(!hasFullHero);
        setHasFullBackground(hasFullHero);
    }, [hasFullHero]);

    return (
        <HeaderBackgroundContext.Provider value={contextValue}>
            {children}
        </HeaderBackgroundContext.Provider>
    );
}

const useHeaderBackground = (): HeaderBackgroundContextType => {
    const context = useContext(HeaderBackgroundContext);
    if (!context) {
        throw new Error("useHeaderBackground must be used within a HeaderBackgroundProvider");
    }
    return context;
};

export { HeaderBackgroundProvider, useHeaderBackground };
